import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { database } from 'helpers/firebase';
import Paragraph from 'components/atoms/Paragraph';
import { ReactComponent as Cloud } from 'assets/icons/cloud.svg';
import { ReactComponent as Document } from 'assets/icons/document.svg';
import { ReactComponent as Photo } from 'assets/icons/photo.svg';
import { ReactComponent as Video } from 'assets/icons/video.svg';
import { ReactComponent as Music } from 'assets/icons/music.svg';
import { ReactComponent as Folder } from 'assets/icons/folder.svg';
import { ReactComponent as Clock } from 'assets/icons/clock.svg';
import { ReactComponent as Edit } from 'assets/icons/edit.svg';

const Wrapper = styled.nav`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  height: 100%;
  width: 300px;
  background-color: ${({ theme }) => theme.gray4};
  border-right: 2px solid ${({ theme }) => theme.gray3};
`;

const Headline = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: space-between;
  margin: 15px 5px 5px;
  width: 100%;
`;

const List = styled.ul`
  display: flex;
  flex-flow: column nowrap;
  list-style-type: none;
  width: 100%;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  opacity: 0;
  margin-left: auto;
  outline: none;
  border-radius: 50%;

  :hover {
    background-color: ${({ theme }) => theme.gray2};
  }

  svg {
    margin-right: 0 !important;
  }
`;

const ListItem = styled.li`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  padding: 0 10px;
  width: 100%;
  height: 32px;
  color: ${({ theme }) => theme.text};
  text-decoration: none;

  ${({ noHover }) =>
    !noHover &&
    css`
      :hover {
        background-color: ${({ theme }) => theme.gray3};
        color: ${({ theme }) => theme.text};
        cursor: pointer;
      }
    `}

  ${({ isactive, theme }) =>
    isactive &&
    css`
      background-color: ${theme.gray3};
      color: ${theme.text};
    `};

  ${({ isindentation }) =>
    isindentation &&
    css`
      padding: 0 10px 0 30px;
    `};

  svg {
    margin-right: 10px;
    fill: ${({ theme }) => theme.textSecondary};
  }

  :hover ${EditButton} {
    opacity: 1;
  }
`;

function Sidebar({ currentPage, isAdmin }) {
  const [folders, setFolders] = useState([]);

  useEffect(() => {
    database.ref('/folders').on('value', (snapshot) => {
      const arr = [];
      if (snapshot.val()) {
        Object.keys(snapshot.val()).forEach((key) => {
          arr.push({ name: snapshot.val()[key], key });
        });
      }

      setFolders(arr);
    });
  }, []);

  const handleEditFolderName = (name, key) => {
    // eslint-disable-next-line
    const newName = window.prompt(`Zmień nazwę folderu\nStara nazwa: ${name}\nNowa nazwa:`, name);

    if (newName && newName !== null) {
      database.ref('/folders').update({
        [key]: newName,
      });
    }
  };

  return (
    <Wrapper>
      <Headline>
        <Paragraph secondary>Chmura</Paragraph>
      </Headline>
      <List>
        <ListItem as={Link} to="/f/ostatnie" isactive={currentPage === 'ostatnie 24h' ? 1 : 0}>
          <Clock />
          <Paragraph>Ostatnie 24h</Paragraph>
        </ListItem>
        <ListItem as={Link} to="/" isactive={currentPage === 'Wszystkie pliki' ? 1 : 0}>
          <Cloud />
          <Paragraph>Wszystkie pliki</Paragraph>
        </ListItem>
        <ListItem as={Link} to="/f/dokumenty" isactive={currentPage === 'Dokumenty' ? 1 : 0}>
          <Document />
          <Paragraph>Dokumenty</Paragraph>
        </ListItem>
        <ListItem as={Link} to="/f/zdjęcia" isactive={currentPage === 'Zdjęcia' ? 1 : 0}>
          <Photo />
          <Paragraph>Zdjęcia</Paragraph>
        </ListItem>
        <ListItem as={Link} to="/f/muzyka" isactive={currentPage === 'Muzyka' ? 1 : 0}>
          <Music />
          <Paragraph>Muzyka</Paragraph>
        </ListItem>
        <ListItem as={Link} to="/f/wideo" isactive={currentPage === 'Wideo' ? 1 : 0}>
          <Video />
          <Paragraph>Wideo</Paragraph>
        </ListItem>
        <ListItem noHover>
          <Folder />
          <Paragraph>Foldery</Paragraph>
        </ListItem>
        {folders.map(({ name, key }) => (
          <ListItem
            key={key}
            as={Link}
            to={`/f/${key}`}
            isactive={currentPage === name ? 1 : 0}
            isindentation={1}
          >
            <Folder />
            <Paragraph>{name}</Paragraph>
            {isAdmin ? (
              <EditButton type="button" onClick={() => handleEditFolderName(name, key)}>
                <Edit />
              </EditButton>
            ) : null}
          </ListItem>
        ))}
      </List>
    </Wrapper>
  );
}

Sidebar.propTypes = {
  currentPage: PropTypes.string.isRequired,
  isAdmin: PropTypes.bool,
};

Sidebar.defaultProps = {
  isAdmin: false,
};

export default Sidebar;
